import { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import { spinnerRing } from '../utils/bookingUtils';
import { fetchClientRoles } from '../utils/posClientUtil';
import { subscribePosRoleOverrides, savePosRoleOverride, resetPosRoleOverride } from '../utils/posUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { isStoreAdminRole } from '../utils/roleUtil';
import { POS_PERMISSIONS } from '../constants/posConst';
import '../css/Pos.css';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/**
 * Two permission lists hold the same rights, whatever their order.
 *
 * @param {string[]} a
 * @param {string[]} b
 * @returns {boolean}
 */
const sameRights = (a = [], b = []) => a.length === b.length && a.every((key) => b.includes(key));

/**
 * Config -> Staff Access: what each of this club's staff roles may do HERE.
 *
 * STORE ADMINS ONLY (PosConfigScreen hides the tab from everyone else). The
 * roles themselves — their names, and which account holds which — are set by
 * the Website Pal administrator; this screen only re-ticks their rights.
 *
 * Changes apply to THIS CLUB ONLY. They are saved as an override in the
 * club's own data, so the administrator's default is kept and "Reset to
 * default" can always bring it back.
 *
 * The store admin role itself (ticked "Store admin", or simply named "Admin")
 * is READ-ONLY: shown with every right ticked and nothing to change. It always
 * has full access, and letting it be narrowed from here is how a club locks
 * itself out.
 *
 * @returns {JSX.Element}
 */
function PosStaffAccessForm() {
  const { clientId } = useConfig();
  const { staffEmail } = usePosSession();
  const [roles, setRoles] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // roleId -> the rights ticked on screen, for roles being edited.
  const [drafts, setDrafts] = useState({});
  const [busyRole, setBusyRole] = useState('');
  const [modal, setModal] = useState(null);

  useEffect(() => {
    let active = true;
    fetchClientRoles(clientId)
      .then((list) => { if (active) setRoles(list); })
      .catch((error) => { if (active) { setLoadError(error); setRoles([]); } });
    const stop = subscribePosRoleOverrides(clientId, setOverrides, (error) => { setLoadError(error); setOverrides({}); });
    return () => { active = false; stop(); };
  }, [clientId]);

  const staffRoles = useMemo(() => (roles || []).filter((role) => !isStoreAdminRole(role)), [roles]);
  const adminRoles = useMemo(() => (roles || []).filter((role) => isStoreAdminRole(role)), [roles]);

  if (roles === null || overrides === null) {
    return <div className="pos-loading">{spinnerRing('Loading staff roles…')}</div>;
  }

  /**
   * What a role allows at this club right now: the override if there is one,
   * otherwise the administrator's default.
   *
   * @param {object} role
   * @returns {string[]}
   */
  const effective = (role) => overrides[role.id] || role.permissions;

  /**
   * Ticks or un-ticks one right on a role's draft.
   *
   * @param {object} role
   * @param {string} key
   * @returns {void}
   */
  const toggle = (role, key) => {
    setDrafts((current) => {
      const base = current[role.id] || effective(role);
      const next = base.includes(key) ? base.filter((existing) => existing !== key) : [...base, key];
      return { ...current, [role.id]: next };
    });
  };

  /**
   * Saves one role's rights for this club.
   *
   * @param {object} role
   * @returns {Promise<void>}
   */
  const handleSave = async (role) => {
    const chosen = drafts[role.id];
    setBusyRole(role.id);
    try {
      // Choosing exactly the default is saved as "no override", so the role
      // keeps following the administrator's default if that changes later.
      if (sameRights(chosen, role.permissions)) await resetPosRoleOverride(clientId, role, staffEmail);
      else await savePosRoleOverride(clientId, role, chosen, staffEmail);
      setDrafts((current) => { const next = { ...current }; delete next[role.id]; return next; });
      setModal({
        type: 'success',
        title: 'Saved',
        message: `The ${role.name} role's rights are updated. Staff with this role see the change the next time they sign in or refresh.`,
      });
    } catch (error) {
      console.error('Saving role rights failed:', error);
      setModal({ type: 'error', title: 'Could not save', message: describeWriteError(error, { what: 'these rights', path: `posTenants/${clientId}/roleOverrides` }) });
    } finally {
      setBusyRole('');
    }
  };

  /**
   * Puts a role back on the administrator's default.
   *
   * @param {object} role
   * @returns {Promise<void>}
   */
  const handleReset = async (role) => {
    setBusyRole(role.id);
    try {
      await resetPosRoleOverride(clientId, role, staffEmail);
      setDrafts((current) => { const next = { ...current }; delete next[role.id]; return next; });
    } catch (error) {
      console.error('Resetting role rights failed:', error);
      setModal({ type: 'error', title: 'Could not reset', message: describeWriteError(error, { what: 'these rights', path: `posTenants/${clientId}/roleOverrides` }) });
    } finally {
      setBusyRole('');
    }
  };

  return (
    <div className="pos-config">
      <section className="pos-section">
        <div className="pos-section-head">
          <h2>Staff Access</h2>
          <p>
            Choose what each staff role can do at this store. Changes apply to this store only and
            take effect the next time staff sign in or refresh. Roles and who holds them are set by
            your system administrator.
          </p>
        </div>

        {loadError && (
          <p className="pos-empty-line">Some role details could not be loaded: {loadError.message}</p>
        )}

        <div className="pa-role-list">
          {/* READ-ONLY. Every right shown ticked and disabled, with no Save —
              the store admin always has full access. */}
          {adminRoles.map((role) => (
            <div className="pa-role-card ps-role-readonly" key={role.id} aria-disabled="true">
              <div className="pa-role-card-head">
                <span className="pa-role-name">
                  {role.name}
                  <span className="ps-role-badge ps-role-badge-admin">Store admin</span>
                  <span className="ps-role-badge ps-role-badge-muted">Read-only</span>
                </span>
              </div>

              <div className="ps-rights-grid">
                {POS_PERMISSIONS.map(({ key, label }) => (
                  <label key={key} className="pa-check pa-role-perm">
                    <input type="checkbox" checked readOnly disabled />
                    <span>{label}</span>
                  </label>
                ))}
              </div>

              <p className="pa-field-hint">Always has full access. This role cannot be changed here.</p>
            </div>
          ))}

          {staffRoles.length === 0 && (
            <p className="pos-empty-line">
              No staff roles yet. Ask your system administrator to add one (for example &ldquo;Staff&rdquo; or &ldquo;Cashier&rdquo;).
            </p>
          )}

          {staffRoles.map((role) => {
            const shown = drafts[role.id] || effective(role);
            const customised = Boolean(overrides[role.id]);
            const dirty = Boolean(drafts[role.id]) && !sameRights(drafts[role.id], effective(role));
            const busy = busyRole === role.id;
            return (
              <div className="pa-role-card" key={role.id}>
                <div className="pa-role-card-head">
                  <span className="pa-role-name">
                    {role.name}
                    <span className={`ps-role-badge${customised ? '' : ' ps-role-badge-muted'}`}>
                      {customised ? 'Customised for this store' : 'Default'}
                    </span>
                  </span>
                  <div className="pa-role-card-actions">
                    {customised && (
                      <button type="button" className="pa-btn pa-btn-quiet" onClick={() => handleReset(role)} disabled={busy}>
                        Reset to default
                      </button>
                    )}
                    <button type="button" className="pa-btn pa-btn-primary" onClick={() => handleSave(role)} disabled={busy || !dirty}>
                      {busy ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>

                <div className="ps-rights-grid">
                  {POS_PERMISSIONS.map(({ key, label }) => (
                    <label key={key} className="pa-check pa-role-perm">
                      <input
                        type="checkbox"
                        checked={shown.includes(key)}
                        onChange={() => toggle(role, key)}
                        disabled={busy}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>

                {shown.length === 0 && (
                  <p className="pa-field-hint">Nothing ticked — staff with this role will not be able to open any screen.</p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <Modal
        isOpen={Boolean(modal)}
        onClose={() => setModal(null)}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
      />
    </div>
  );
}

export default PosStaffAccessForm;
